/**
 * mycraftzStages.js — Static showcase data for the 5 MyCraftz stages
 * (Pages 5–9 of the master mockup).
 *
 * For Phase 1 visual fidelity we use the canonical "Atomic Habits → Identity-Based
 * Habits" example shown in the mockup. Real per-user data flows live in the
 * existing MyCraftzInstallOverlay + /api/mycraftz-install endpoints.
 */

export const STAGE_ORDER = ["jigyasa", "bodha", "sankalpa", "sadhana", "siddhi"];

export const STAGES = {
  jigyasa: {
    num: 1,
    key: "jigyasa",
    title: "JIGYASA",
    devanagari: "जिज्ञासा",
    sub: "Curiosity",
    next: "bodha",
    nextCta: "Continue to Understanding",
    hero: "reader-tree",      // painterly key
    selectedBook: { title: "Atomic Habits", author: "James Clear" },
    powerfulIdea: {
      label: "Identity-Based Habits",
      quote: "Your identity shapes your habits, and your habits shape your identity.",
    },
    whyMatters: {
      title: "WHY THIS MATTERS",
      body: "When you focus on becoming a certain type of person, you naturally make choices that align with that identity.",
    },
  },

  bodha: {
    num: 2,
    key: "bodha",
    title: "BODHA",
    devanagari: "बोध",
    sub: "Understanding",
    next: "sankalpa",
    nextCta: "Continue to Commitment",
    hero: "open-manuscript",
    selectedBook: { title: "Atomic Habits", author: "James Clear" },
    insightsTitle: "KEY INSIGHTS FROM ATOMIC HABITS",
    insights: [
      "Identity is the ultimate form of motivation.",
      "Habits are the compound interest of self-improvement.",
      "Focus on systems, not goals.",
      "Make it easy to do good habits and hard to do bad habits.",
      "Small changes, remarkable results.",
    ],
  },

  sankalpa: {
    num: 3,
    key: "sankalpa",
    title: "SANKALPA",
    devanagari: "संकल्प",
    sub: "Commitment",
    next: "sadhana",
    nextCta: "Commit to This Idea",
    hero: "parchment-pledge",
    chooseTitle: "Choose One Idea",
    chooseSub: "Pick the idea that will create the biggest impact in your life right now.",
    options: [
      { id: "identity",   title: "Identity-Based Habits", desc: "Focus on becoming the type of person who gets the results.", selected: true },
      { id: "habitloop",  title: "Habit Loop",            desc: "Cue → Craving → Response → Reward.",                       selected: false },
      { id: "environ",    title: "Environment Design",    desc: "Shape your environment to make good habits easier.",        selected: false },
      { id: "stacking",   title: "Habit Stacking",        desc: "Attach a new habit to an existing one.",                    selected: false },
    ],
  },

  sadhana: {
    num: 4,
    key: "sadhana",
    title: "SADHANA",
    devanagari: "साधना",
    sub: "Practice",
    next: "siddhi",
    nextCta: "View 30-Day Journey",
    hero: "blueprint",
    blueprint: [
      { label: "SUTRA",    sanskrit: "PRINCIPLE",  body: "Become the type of person before achieving the outcome." },
      { label: "TRIGGER",  sanskrit: "NIMITTA",    body: "After brushing my teeth in the morning." },
      { label: "ACTION",   sanskrit: "KRIYA",      body: "Read 2 pages of a book." },
      { label: "DURATION", sanskrit: "KALA",       body: "30 Days." },
      { label: "MEASURE",  sanskrit: "LAKSHYA",    body: "20+ days completed." },
      { label: "REWARD",   sanskrit: "PHALA",      body: "Track streak and feel the growth." },
      { label: "OBSTACLE", sanskrit: "VIGHNA",     body: "Forgetfulness / Laziness." },
      { label: "ANTIDOTE", sanskrit: "UPAYA",      body: "Set 7 AM reminder. Never miss twice." },
    ],
    quote: "Discipline is choosing between what you want now and what you want most.",
    quoteAttr: "— Atomic Habits",
    planDays: 30,
    completedDays: 14,
    progressPct: 47,
  },

  siddhi: {
    num: 5,
    key: "siddhi",
    title: "SIDDHI",
    devanagari: "सिद्धि",
    sub: "Transformation",
    next: null,
    nextCta: "View Full Dashboard",
    hero: "mountain-temple",
    dashboard: {
      metrics: [
        { value: "17",  label: "Day Streak",       icon: "flame" },
        { value: "23",  label: "Ideas Installed",  icon: "book" },
        { value: "14",  label: "Plans Completed",  icon: "check" },
        { value: "87%", label: "Consistency",      icon: "chart" },
      ],
      currentPlan: { name: "Identity-Based Habits", day: 14, total: 30, percent: 47 },
      recentReflection: {
        quote: "Discipline is choosing between what you want now and what you want most.",
        source: "Atomic Habits",
      },
      achievements: [
        { name: "7 Day Streak",       icon: "flame" },
        { name: "First Plan Completed", icon: "trophy" },
        { name: "10 Books Read",      icon: "books" },
        { name: "30 Day Warrior",     icon: "star" },
      ],
    },
  },
};

export const getStage = (key) => STAGES[key] || STAGES.jigyasa;
